import { useState, useEffect, createContext, useContext, ReactNode } from "react"
import { useOnboardingStatus, useCompleteOnboarding, useCompleteTour } from "@/hooks/useOnboarding"
import { OnboardingWizard } from "./OnboardingWizard"
import { GuidedTour } from "./GuidedTour"
import { useAuth } from "@/hooks/useAuth"

interface OnboardingContextValue {
  showTour: boolean
  restartTour: () => void
}

const OnboardingContext = createContext<OnboardingContextValue>({
  showTour: false,
  restartTour: () => {},
})

export const useOnboardingContext = () => useContext(OnboardingContext)

interface OnboardingProviderProps {
  children: ReactNode
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { user } = useAuth()
  const { data: status, isLoading } = useOnboardingStatus()
  const completeOnboarding = useCompleteOnboarding()
  
  const [showWizard, setShowWizard] = useState(false)
  const [showTour, setShowTour] = useState(false)
  const [initialized, setInitialized] = useState(false)

  useEffect(() => {
    if (!isLoading && status && user && !initialized) {
      // Show wizard if onboarding not completed
      if (!status.onboarding_completed) {
        setShowWizard(true)
      }
      // Show tour if onboarding completed but tour not
      else if (!status.tour_completed) {
        setShowTour(true)
      }
      setInitialized(true)
    }
  }, [status, isLoading, user, initialized])

  const handleWizardComplete = () => {
    setShowWizard(false)
    // Start tour after wizard
    setShowTour(true)
  }

  const handleWizardSkip = async () => {
    // Mark onboarding as completed even when skipped
    if (user) {
      await completeOnboarding.mutateAsync({
        monthly_goal: 0,
        hourly_rate: 0,
        weekly_work_hours: 40,
        tax_regime: 'mei',
      })
    }
    setShowWizard(false)
    setShowTour(true)
  }

  const handleTourComplete = () => {
    setShowTour(false)
  }

  const restartTour = () => {
    setShowTour(true)
  }

  return (
    <OnboardingContext.Provider value={{ showTour, restartTour }}>
      {children}
      
      {showWizard && (
        <OnboardingWizard 
          onComplete={handleWizardComplete}
          onSkip={handleWizardSkip}
        />
      )}
      
      {showTour && !showWizard && (
        <GuidedTour onComplete={handleTourComplete} />
      )}
    </OnboardingContext.Provider>
  )
}
